import { Grid } from '@material-ui/core';
import {
  DefaultButton,
  MessageBox,
  PhotoCard,
  useFormHook,
} from 'design-system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import {
  EMISSION_CAR_INFORMATION_STEP,
  trackCurrentStep,
  trackEvent,
} from 'src/analytics';

// Redux
import {
  setTextValues,
  setVehicleRegistryImage,
} from '../../../../store/inspectionForm/actions';
import { TextValuesPayload } from '../../../../store/inspectionForm/slice/inspectionFormSlice';
import { RootState } from '../../../../store/rootReducer';

// Hooks
import { useCompleteCarIdentificationForm } from '../../hooks';

// Validation
import makeCarDataValidation from './makeCarDataValidation';

// Components
import { AlreadyInsuredErrorDialogue } from '../../components';
import useBaseWrapperStyles, {
  minDesktopCardHeight,
} from '../useBaseWrapperStyles';

type FormShape = {
  vinNumber: string;
  licensePlate: string;
  serialMotor: string;
  type: string;
};

export const CarData = ({
  setTextValuesAction,
  vehicleRegistryFilename,
  vehicleRegistryImage,
  setVehicleRegistryImageAction,
  onSubmit,
  textValues,
}: Props): JSX.Element => {
  const classes = useBaseWrapperStyles();
  const [alreadyInsuredDialogueOpen, toggleAlreadyInsuredDialogueOpen] =
    useState(false);

  /** Scroll to the top */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /** Initialize the form values */
  const initialValues: FormShape = {
    vinNumber: textValues.vinNumber,
    licensePlate: textValues.licensePlate,
    serialMotor: textValues.serialMotor,
    type: textValues.type,
  };

  const submitData = useCallback(
    (data: FormShape): void => {
      setTextValuesAction({
        ...data,
        previousDamages: '',
        carImprovements: [],
      });
      trackEvent({
        eventName: EMISSION_CAR_INFORMATION_STEP,
        eventProperties: {},
      });
      trackCurrentStep(EMISSION_CAR_INFORMATION_STEP);
      onSubmit();
    },
    [setTextValuesAction, onSubmit],
  );

  const schema = makeCarDataValidation();
  const {
    handleSubmit,
    providedAllRequired,
    getInputProps,
    updateMultipleInputs,
    errors,
  } = useFormHook<FormShape>(initialValues, submitData, schema);

  const onDialogueRequested = useCallback(
    () => toggleAlreadyInsuredDialogueOpen(true),
    [],
  );
  const {
    licensePlateInputCard,
    vinNumberInputCard,
    dataIsInvalid,
    policySource,
  } = useCompleteCarIdentificationForm({
    getInputProps,
    updateMultipleInputs,
    minDesktopCardHeight,
    onDialogueRequested,
  });

  const buttonIsDisabled = useMemo(
    () =>
      !providedAllRequired() ||
      vehicleRegistryImage === '' ||
      dataIsInvalid ||
      Object.keys(errors).length > 0,
    [providedAllRequired, dataIsInvalid, vehicleRegistryImage, errors],
  );

  const curLicense = getInputProps('licensePlate').value as string;
  const curVin = getInputProps('vinNumber').value as string;

  /**
   * This is a special case where the values need to be saved to the store as they are typed
   */
  useEffect(() => {
    setTextValuesAction({
      ...textValues,
      licensePlate: curLicense,
      vinNumber: curVin,
      serialMotor: curVin,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curLicense, curVin, setTextValuesAction]);

  return (
    <form onSubmit={handleSubmit}>
      <AlreadyInsuredErrorDialogue
        data-testid="already-insured-dialogue"
        open={alreadyInsuredDialogueOpen}
        onCloseRequested={(): void => toggleAlreadyInsuredDialogueOpen(false)}
        source={policySource}
        licensePlate={curLicense}
        vinNumber={curVin}
        motorNumber={curVin}
      />
      <Grid container justifyContent="center">
        <Grid
          container
          spacing={2}
          item
          xs={12}
          md={7}
          className={classes.pageContainer}
        >
          <Grid item xs={12}>
            <MessageBox
              message={
                <div>
                  Asegúrate que los datos del Placa, VIN y Motor estén correctos
                  para evitar inconvenientes con el tránsito.
                </div>
              }
              variant="info"
              hideCloseButton
            />
          </Grid>
          {/* License Section */}
          <Grid item xs={12} md={6}>
            {licensePlateInputCard}
          </Grid>

          {/* Vin Section */}
          <Grid item xs={12} md={6}>
            {vinNumberInputCard}
          </Grid>

          {/* Photo section */}
          <Grid item xs={12} md={6}>
            <PhotoCard
              id="sales-web-inspection-button-registrycard"
              data-testid="photo-card"
              title="Foto del registro vehicular"
              subTitle="Incluya una foto de su registro vehicular"
              onLocalImageCaptured={setVehicleRegistryImageAction}
              base64String={vehicleRegistryImage}
              fileName={vehicleRegistryFilename}
              confirmationMessage="¿Estás seguro que deseas eliminar el registro vehicular?"
            />
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12} container justifyContent="center">
            <div className={classes.buttonContainer}>
              <DefaultButton
                id="sales-web-inspection-button-next"
                data-testid="submit-button"
                fullWidth
                type="submit"
                disabled={buttonIsDisabled}
              >
                Siguiente
              </DefaultButton>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  vehicleRegistryFilename: string;
  vehicleRegistryImage: string;
  textValues: TextValuesPayload;
} => ({
  vehicleRegistryFilename: state.inspectionForm.vehicleRegistryFileName,
  vehicleRegistryImage: state.inspectionForm.vehicleRegistryImage,
  textValues: state.inspectionForm.textValues,
});

const reduxActions = {
  setTextValuesAction: setTextValues,
  setVehicleRegistryImageAction: setVehicleRegistryImage,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onSubmit: () => void;
}

export default connector(CarData);
